import {
  HUB_LAYOUT_HINTS,
  HUB_SECTION_TYPES,
  type HubKind,
  type HubLayoutHint,
  type HubSectionType,
} from './schema'

export const HUB_COMPONENT_REGISTRY_VERSION = '2.0.0'

export interface HubComponentPromptDefinition {
  oneLine: string
  use: string
  avoid: string
}

export interface HubComponentDefinition {
  type: HubSectionType
  componentName: string
  purpose: string
  requiredFields: string[]
  referenceFields: string[]
  allowedLayoutHints: HubLayoutHint[]
  supportedHubKinds: HubKind[]
  prompt: HubComponentPromptDefinition
}

export const HUB_COMPONENT_CATALOG = {
  hero: {
    type: 'hero',
    componentName: 'HubHero',
    purpose:
      'Page headline, breadcrumbs, byline, meta, and optional hero image.',
    requiredFields: ['id', 'type', 'title'],
    referenceFields: ['sourceReportIds', 'sourceProductIds'],
    allowedLayoutHints: ['full'],
    supportedHubKinds: ['informational', 'transactional'],
    prompt: {
      oneLine: 'Page H1, kicker, breadcrumbs, optional byline and meta line.',
      use: 'Required. Open with a tight H1 and a one-sentence dek. metaLine is the trust signal: "X reports, Y sources" when you have the numbers.',
      avoid: 'No marketing copy. No more than one sentence in dek.',
    },
  },
  intro: {
    type: 'intro',
    componentName: 'HubIntro',
    purpose: 'Editorial introduction paragraphs.',
    requiredFields: ['id', 'type', 'body'],
    referenceFields: ['sourceReportIds'],
    allowedLayoutHints: [...HUB_LAYOUT_HINTS],
    supportedHubKinds: ['informational', 'transactional'],
    prompt: {
      oneLine:
        'Editorial introduction. Paragraph breaks as \\n\\n inside the body string.',
      use: 'Frame the reader problem and synthesize across assigned evidence. Inline anchor links into reports are encouraged.',
      avoid:
        'Do not repeat the hero title verbatim. Do not begin with "In this guide".',
    },
  },
  anchorLinkList: {
    type: 'anchorLinkList',
    componentName: 'HubAnchorLinkList',
    purpose:
      'Visible carousel navigation to the assigned reports, rendered as one card per report.',
    requiredFields: ['id', 'type', 'links'],
    referenceFields: ['links.href'],
    allowedLayoutHints: ['full', 'twoColumn', 'compactGrid'],
    // Report-card navigation is informational-only. Transactional pages are
    // product-first and reach the reports via the sibling band, not a carousel.
    supportedHubKinds: ['informational'],
    prompt: {
      oneLine: 'One carousel card for each assigned report.',
      use: 'Use the raw report href from the input, for example /reports/report-slug. Include each assigned report once.',
      avoid:
        'Do not use hash anchors like #quick-answer, #top-picks, or #how-we-picked. Do not emit multiple links for different sections of the same report.',
    },
  },
  topPicks: {
    type: 'topPicks',
    componentName: 'HubTopPicks',
    purpose: 'Short list of product recommendations with reasons.',
    requiredFields: ['id', 'type', 'picks'],
    referenceFields: ['picks.productId', 'picks.sourceReportId'],
    allowedLayoutHints: ['full', 'twoColumn', 'featureAside'],
    supportedHubKinds: ['transactional'],
    prompt: {
      oneLine: 'Curated product picks with role labels and decisive reasons.',
      use: 'Transactional only. One pick per assigned report when the evidence supports it. label comes from the source report roleLabel.',
      avoid:
        'No flattening. Drop a pick if you cannot write a comparative reason grounded in a source report.',
    },
  },
  productRail: {
    type: 'productRail',
    componentName: 'HubProductRail',
    purpose: 'Horizontal product collection resolved from product IDs.',
    requiredFields: ['id', 'type', 'productIds'],
    referenceFields: ['productIds'],
    allowedLayoutHints: ['full', 'twoColumn', 'featureAside'],
    supportedHubKinds: ['transactional'],
    prompt: {
      oneLine: 'Horizontal product strip beyond the top picks.',
      use: 'Transactional only. Browsable products that did not become top picks. 6 to 10 productIds.',
      avoid: 'Do not repeat the topPicks selection.',
    },
  },
  reportGrid: {
    type: 'reportGrid',
    componentName: 'HubReportGrid',
    purpose: 'Linked reports that belong to this guide page.',
    requiredFields: ['id', 'type', 'items'],
    referenceFields: ['items.reportId'],
    allowedLayoutHints: ['full', 'twoColumn', 'compactGrid', 'denseList'],
    // Deprecated: report-card grids are report-as-content, which now belongs to
    // informational (via anchorLinkList). No kind generates reportGrid; the
    // catalog entry and renderer case remain only to render legacy specs.
    supportedHubKinds: [],
    prompt: {
      oneLine: 'Linked report cards with generated contextual blurbs.',
      use: 'Include every assigned report unless the blueprint explicitly routes long-tail reports through sourcesSummary. Each item should include reportId and a blurb: 1-2 concise sentences explaining why this report matters in the current guide context.',
      avoid:
        'Do not invent reports. items[].reportId must match an input report id. Do not copy report excerpts or verdict summaries verbatim.',
    },
  },
  guideSteps: {
    type: 'guideSteps',
    componentName: 'HubGuideSteps',
    purpose: 'Ordered how-to or decision steps.',
    requiredFields: ['id', 'type', 'steps'],
    referenceFields: [],
    allowedLayoutHints: ['full', 'twoColumn', 'featureAside'],
    supportedHubKinds: ['informational'],
    prompt: {
      oneLine:
        'Ordered how-to or decision steps with label and 2-3 sentence description.',
      use: 'Informational only. 4 to 6 steps. Anchor each step to a specific supporting report section when it helps.',
      avoid:
        'Do not pad to hit step count. Three sharp steps beat six vague ones.',
    },
  },
  guidePathways: {
    type: 'guidePathways',
    componentName: 'HubGuidePathways',
    purpose:
      'Full-width report-backed callouts that map reader problems, occasions, or use cases to assigned reports.',
    requiredFields: ['id', 'type', 'pathways'],
    referenceFields: ['pathways.reportId'],
    allowedLayoutHints: ['full', 'twoColumn', 'featureAside'],
    supportedHubKinds: ['informational'],
    prompt: {
      oneLine:
        'Report-backed pathways: heading, subheading, body, and one linked report per item.',
      use: 'Informational only. Reverse engineer 3 to 6 real reader situations from the assigned reports, then map each to exactly one reportId. Write synthesis, not a table of contents.',
      avoid:
        'Do not use the words "pain point" as a heading. Do not invent reports, products, rankings, or generic occasions unsupported by the assigned reports.',
    },
  },
  decisionBlock: {
    type: 'decisionBlock',
    componentName: 'HubDecisionBlock',
    purpose: 'Prominent editorial decision callout with one internal link.',
    requiredFields: ['id', 'type', 'tone', 'kicker', 'linkLabel', 'linkHref'],
    referenceFields: [],
    allowedLayoutHints: ['full', 'twoColumn', 'featureAside'],
    supportedHubKinds: ['transactional'],
    prompt: {
      oneLine:
        'Prominent callout with one internal link. supportText takes inline links too.',
      use: 'Place at moments the reader is ready to act: jump to a specific report, companion guide, or comparison anchor.',
      avoid: 'Do not stack three consecutive decisionBlocks. Distribute them.',
    },
  },
  concernLinks: {
    type: 'concernLinks',
    componentName: 'HubConcernLinks',
    purpose: 'Chips for user concerns, pain points, or decision paths.',
    requiredFields: ['id', 'type', 'links'],
    referenceFields: [],
    allowedLayoutHints: ['full', 'compactGrid', 'denseList'],
    supportedHubKinds: ['informational', 'transactional'],
    prompt: {
      oneLine:
        'Chips for concerns, personas, or pain points. Each chip links to a report anchor.',
      use: 'Use when more than one user concern or tradeoff axis matters.',
      avoid:
        'Do not use as a generic table of contents. Do not duplicate reportGrid.',
    },
  },
  comparisonTable: {
    type: 'comparisonTable',
    componentName: 'HubComparisonTable',
    purpose: 'Compact comparison matrix.',
    requiredFields: ['id', 'type', 'columns', 'rows'],
    referenceFields: [],
    allowedLayoutHints: ['full', 'featureAside'],
    supportedHubKinds: ['transactional'],
    prompt: {
      oneLine: 'Comparison matrix across picks.',
      use: 'Transactional only. 3 to 5 columns and 4 to 6 meaningful rows. Every row.values length must equal columns length.',
      avoid:
        'Skip if you cannot fill every cell with a fact. null is allowed only when the dimension genuinely does not apply.',
    },
  },
  faq: {
    type: 'faq',
    componentName: 'HubFaq',
    purpose: 'Frequently asked questions.',
    requiredFields: ['id', 'type', 'items'],
    referenceFields: [],
    allowedLayoutHints: ['full', 'twoColumn', 'denseList'],
    supportedHubKinds: ['informational', 'transactional'],
    prompt: {
      oneLine: 'Frequently asked questions. answer takes inline anchor links.',
      use: "8 to 12 of the most relevant questions, 2-3 sentence answers. Pull from report whatToKnow and the reader's likely follow-ups.",
      avoid: 'Do not invent obvious filler questions.',
    },
  },
  sourcesSummary: {
    type: 'sourcesSummary',
    componentName: 'HubSourcesSummary',
    purpose: 'Source count and evidence summary.',
    requiredFields: ['id', 'type', 'totalSources'],
    referenceFields: [],
    allowedLayoutHints: ['full', 'compactGrid'],
    supportedHubKinds: ['informational', 'transactional'],
    prompt: {
      oneLine: 'Trust signal: how much evidence backs the page.',
      use: 'Always include. Sum evidence counts from report evidence records.',
      avoid: 'Do not inflate counts.',
    },
  },
  ctaBand: {
    type: 'ctaBand',
    componentName: 'HubCtaBand',
    purpose: 'Final internal call to action.',
    requiredFields: ['id', 'type', 'primary'],
    referenceFields: [],
    allowedLayoutHints: ['full'],
    supportedHubKinds: ['transactional'],
    prompt: {
      oneLine: 'Final internal call to action.',
      use: 'Transactional only. Closer. primary link to the strongest report; optional secondary link to informational sibling.',
      avoid: 'CTAs target internal pages, not retailers.',
    },
  },
  pullQuote: {
    type: 'pullQuote',
    componentName: 'HubPullQuote',
    purpose:
      'Editorial pull quote with attribution. Use for a single high-leverage sentence from a report or expert source.',
    requiredFields: ['id', 'type', 'quote'],
    referenceFields: ['sourceReportId'],
    allowedLayoutHints: ['full', 'twoColumn', 'featureAside'],
    supportedHubKinds: ['informational', 'transactional'],
    prompt: {
      oneLine:
        'Editorial pull quote. Single high-leverage sentence with attribution.',
      use: 'Use when a sentence from a report, expert review, or community signal carries weight that prose dilutes.',
      avoid:
        'No invented quotes. The quote must be sourced from a report, product insight, or webSearch result.',
    },
  },
  statCallout: {
    type: 'statCallout',
    componentName: 'HubStatCallout',
    purpose:
      'Trust-signal stat tiles. 2-4 value+label tiles in a row (sources reviewed, products analyzed, average savings).',
    requiredFields: ['id', 'type', 'stats'],
    referenceFields: [],
    allowedLayoutHints: ['full', 'compactGrid'],
    // Deprecated: stat tiles are cut from both kinds. The catalog entry and
    // renderer case remain only to render legacy specs.
    supportedHubKinds: [],
    prompt: {
      oneLine: 'Trust-signal stat tiles. 2-4 value+label tiles in a row.',
      use: 'Surface evidence volume near the top. Numbers must come from evidence counts or product counts.',
      avoid: 'No invented numbers.',
    },
  },
  methodologyCard: {
    type: 'methodologyCard',
    componentName: 'HubMethodologyCard',
    purpose:
      "Short callout explaining how picks were evaluated, linked to a report's how-we-picked anchor. Adds credibility inline.",
    requiredFields: ['id', 'type', 'body', 'linkLabel', 'linkHref'],
    referenceFields: ['sourceReportId'],
    allowedLayoutHints: ['full', 'twoColumn', 'featureAside'],
    supportedHubKinds: ['transactional'],
    prompt: {
      oneLine:
        "Short callout explaining how picks were evaluated, linked to a report's how-we-picked anchor.",
      use: 'Use when the reader needs to know where the credibility comes from.',
      avoid:
        'Do not duplicate the intro or decisionBlock. methodologyCard is specifically about method.',
    },
  },
} satisfies {
  [Type in HubSectionType]: HubComponentDefinition & { type: Type }
}

export const HUB_COMPONENT_REGISTRY: readonly HubComponentDefinition[] =
  HUB_SECTION_TYPES.map((type) => HUB_COMPONENT_CATALOG[type])

const HUB_COMPONENT_DEFINITIONS_BY_TYPE = new Map(
  HUB_COMPONENT_REGISTRY.map((definition) => [definition.type, definition])
)

export function listHubComponentDefinitions(): readonly HubComponentDefinition[] {
  return HUB_COMPONENT_REGISTRY
}

export function listHubComponentDefinitionsForKind(
  hubKind: HubKind
): readonly HubComponentDefinition[] {
  return HUB_COMPONENT_REGISTRY.filter((definition) =>
    definition.supportedHubKinds.includes(hubKind)
  )
}

export function getHubComponentDefinition(
  type: HubSectionType
): HubComponentDefinition {
  const definition = HUB_COMPONENT_DEFINITIONS_BY_TYPE.get(type)

  if (!definition) {
    throw new Error(`Unknown hub component type: ${type}`)
  }

  return definition
}

export function listHubSectionTypes(): readonly HubSectionType[] {
  return HUB_SECTION_TYPES
}

export function listHubSectionTypesForKind(
  hubKind: HubKind
): readonly HubSectionType[] {
  return listHubComponentDefinitionsForKind(hubKind).map(
    (definition) => definition.type
  )
}

export function renderHubComponentPromptCatalog(
  types: readonly HubSectionType[]
): string {
  return types
    .map((type) => {
      const entry = getHubComponentDefinition(type)
      return [
        `## ${entry.type}`,
        `  ${entry.prompt.oneLine}`,
        `  USE: ${entry.prompt.use}`,
        `  AVOID: ${entry.prompt.avoid}`,
      ].join('\n')
    })
    .join('\n\n')
}
